/**
 * InsightPro™ — Cash Flow Statement (Multi-Entity Finance only)
 *
 * Indirect method:
 *   Net Income (EBITDA proxy)
 *   + Depreciation & Amortisation (non-cash add-back)
 *   ± Δ Working Capital (Receivables, Inventory, Payables) — needs ≥2 BS periods
 *   = Operating Cash Flow
 *   ± Investing (Δ Non-current Assets)                     — needs ≥2 BS periods
 *   ± Financing (Δ Long-term debt + Δ Equity contributed)  — needs ≥2 BS periods
 *   = Net Change in Cash → Ending Cash Balance
 *
 * Currently only one BS period (2026-03) is ingested, so working capital,
 * investing, and financing sections show a "needs multi-period BS" banner.
 * Operating section runs on EBITDA proxy alone.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchFinancePeriods, fetchFinanceBrands,
  fetchCashFlow, fetchCashFlowTrend,
} from '../utils/api';

const fmtMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return (v / 1_000_000).toFixed(2) + 'M';
  if (abs >= 1_000)     return (v / 1_000).toFixed(0) + 'K';
  return v.toFixed(0);
};
const fmtFull = (v) => (v == null || isNaN(v)) ? '—' :
  v.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtSigned = (v) => {
  if (v == null || isNaN(v)) return '—';
  return (v > 0 ? '+' : '') + fmtMoney(v);
};
const cashColor = (v) => (v == null) ? 'var(--page-muted)' : (v >= 0 ? 'var(--green-l)' : 'var(--red)');

const S = {
  page:        { padding: '20px 28px', maxWidth: 1500 },
  header:      { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 },
  title:       { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--page-text)', letterSpacing: '-0.02em' },
  sub:         { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginTop: 4, letterSpacing: '0.1em', textTransform: 'uppercase' },
  selectorBar: { display: 'flex', gap: 12, alignItems: 'flex-end' },
  label:       { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 5 },
  select:      { background: 'var(--page)', border: '1px solid var(--card-border)', padding: '8px 12px', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--page-text)', outline: 'none', borderRadius: 2, minWidth: 140 },
  kpiGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 },
  kpiCard:     { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '16px 20px' },
  kpiLabel:    { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 8 },
  kpiVal:      { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--page-text)', lineHeight: 1 },
  kpiSub:      { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', marginTop: 6 },
  card:        { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '16px 20px', marginBottom: 16 },
  cardTitle:   { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 12 },
  cardTotal:   { fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, color: 'var(--gold)', float: 'right' },
  row:         { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px', borderBottom: '1px solid var(--card-border)', fontSize: 13 },
  rowLbl:      { color: 'var(--page-text)' },
  rowAmt:      { fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 500 },
  rowSubLbl:   { color: 'var(--page-muted)', fontSize: 12, paddingLeft: 18 },
  rowTotal:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 4px', borderTop: '2px solid var(--gold)', borderBottom: '1px solid var(--card-border)', marginTop: 4, fontSize: 14, fontWeight: 700 },
  rowTotalAmt: { fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 700 },
  banner:      { padding: '12px 16px', background: 'rgba(200,149,32,0.08)', border: '1px solid rgba(200,149,32,0.25)', borderRadius: 4, marginBottom: 16, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--amber)', lineHeight: 1.5 },
  errorBox:    { padding: 14, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--red)', background: 'rgba(176,40,40,0.08)', border: '1px solid rgba(176,40,40,0.2)', borderRadius: 2, marginBottom: 16 },
  loadingBox:  { padding: 28, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' },
  placeholder: { padding: 16, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', fontStyle: 'italic' },
};

function Row({ label, value, sub, indent = false, total = false }) {
  if (total) {
    return (
      <div style={S.rowTotal}>
        <div style={S.rowLbl}>{label}</div>
        <div style={{ ...S.rowTotalAmt, color: cashColor(value) }}>{fmtSigned(value)}</div>
      </div>
    );
  }
  return (
    <div style={S.row}>
      <div style={indent ? S.rowSubLbl : S.rowLbl}>
        {label}
        {sub && <span style={{ marginLeft: 8, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)' }}>{sub}</span>}
      </div>
      <div style={{ ...S.rowAmt, color: cashColor(value) }}>{fmtSigned(value)}</div>
    </div>
  );
}

function TrendChart({ trend }) {
  if (!trend || trend.length < 2) return <div style={S.loadingBox}>Need ≥ 2 periods to plot trend.</div>;
  const W = 800, H = 200, pad = { l: 56, r: 12, t: 14, b: 28 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const xStep = innerW / (trend.length - 1);
  const xAt   = (i) => pad.l + i * xStep;
  const vals  = trend.map(t => t.ebitda);
  const maxV  = Math.max(...vals, 1);
  const minV  = Math.min(...vals, 0);
  const span  = maxV - minV || 1;
  const yAt   = (v) => pad.t + (1 - (v - minV) / span) * innerH;
  const yZero = yAt(0);

  // Area fill — positive only
  const areaPath = trend.map((t, i) =>
    `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(Math.max(t.ebitda, 0))}`
  ).join(' ') + ` L ${xAt(trend.length - 1)} ${yZero} L ${xAt(0)} ${yZero} Z`;

  const linePath = trend.map((t, i) =>
    `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(t.ebitda)}`
  ).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {[0, 0.25, 0.5, 0.75, 1].map(g => {
        const y = pad.t + g * innerH;
        return <line key={g} x1={pad.l} x2={W - pad.r} y1={y} y2={y} stroke="var(--card-border)" strokeWidth={0.5} />;
      })}
      {/* Zero line */}
      <line x1={pad.l} x2={W - pad.r} y1={yZero} y2={yZero} stroke="var(--page-muted)" strokeWidth={0.8} strokeDasharray="2,2" />
      {trend.map((t, i) => (
        <text key={t.periodKey} x={xAt(i)} y={H - 8} fontSize={9} fill="var(--page-muted)" textAnchor="middle"
          fontFamily="var(--font-mono)">{t.periodKey.slice(2)}</text>
      ))}
      {[0, 0.5, 1].map(g => {
        const v = minV + (1 - g) * span;
        return (
          <text key={g} x={pad.l - 6} y={pad.t + g * innerH + 3} fontSize={9} fill="var(--page-muted)"
            textAnchor="end" fontFamily="var(--font-mono)">{fmtMoney(v)}</text>
        );
      })}
      <path d={areaPath} fill="rgba(143,190,95,0.12)" />
      <path d={linePath} fill="none" stroke="var(--green-l)" strokeWidth={2} />
      {trend.map((t, i) => (
        <circle key={t.periodKey} cx={xAt(i)} cy={yAt(t.ebitda)} r={2.5} fill="var(--green-l)" />
      ))}
    </svg>
  );
}

export default function CashFlow() {
  const [periods, setPeriods]   = useState([]);
  const [period,  setPeriod]    = useState(null);
  const [brands,  setBrands]    = useState({ brands: ['Consolidated'], canPickBrand: false });
  const [brand,   setBrand]     = useState('Consolidated');
  const [cf,      setCf]        = useState(null);
  const [trend,   setTrend]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [err,     setErr]       = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, b] = await Promise.all([fetchFinancePeriods(), fetchFinanceBrands()]);
        if (cancelled) return;
        setPeriods(p.periods || []);
        setPeriod(p.latest || (p.periods?.[0]?.periodKey ?? null));
        setBrands(b);
        setBrand(b.canPickBrand ? 'Consolidated' : (b.brands[0] || 'Consolidated'));
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.error || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!period) return;
    let cancelled = false;
    setLoading(true); setErr(null);
    const apiBrand = brand === 'Consolidated' ? 'all' : brand;
    Promise.all([
      fetchCashFlow(period, apiBrand),
      fetchCashFlowTrend(period, apiBrand, { months: 12 }),
    ])
      .then(([c, t]) => {
        if (cancelled) return;
        setCf(c); setTrend(t); setLoading(false);
      })
      .catch(e => { if (!cancelled) { setErr(e.response?.data?.error || e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [period, brand]);

  const periodOptions = useMemo(() =>
    [...periods].sort((a, b) => (b.periodKey || '').localeCompare(a.periodKey || '')),
    [periods]
  );

  const op = cf?.operating || {};
  const inv = cf?.investing || {};
  const fin = cf?.financing || {};
  const wcDetails = op.workingCapitalDetails;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Cash Flow Statement</div>
          <div style={S.sub}>
            {brand === 'Consolidated' ? 'Consolidated · all brands' : `${brand} only`}
            {' · '}{period || '—'}
            {' · indirect method'}
          </div>
        </div>
        <div style={S.selectorBar}>
          {brands.canPickBrand && (
            <div>
              <div style={S.label}>Brand</div>
              <select style={S.select} value={brand} onChange={e => setBrand(e.target.value)}>
                {brands.brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={S.label}>Period</div>
            <select style={S.select} value={period || ''} onChange={e => setPeriod(e.target.value)}>
              {periodOptions.map(p => (
                <option key={p.periodKey} value={p.periodKey}>{p.periodKey}{p.monthName ? ` · ${p.monthName}` : ''}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {err && <div style={S.errorBox}>⚠ {err}</div>}
      {loading && <div style={S.loadingBox}>Loading…</div>}

      {!loading && cf && (
        <>
          {!cf.hasPriorBS && (
            <div style={S.banner}>
              ⚠ Working capital, investing, and financing sections require <b>≥ 2 Balance Sheet periods</b>.
              Currently only {cf.bsPeriodCount} BS period{cf.bsPeriodCount === 1 ? '' : 's'} ingested
              {cf.priorBSKey ? ` (latest two: ${cf.priorBSKey} → ${period})` : ''}.
              Operating section uses <b>EBITDA</b> as a proxy for operating cash flow until the multi-period
              BS pipeline lands.
            </div>
          )}

          {/* KPI strip */}
          <div style={S.kpiGrid}>
            <KpiTile label="Operating Cash Flow" value={fmtMoney(op.operatingCashFlow)} sub="EBITDA + D&A + ΔWC"
                     valueColor={cashColor(op.operatingCashFlow)} />
            <KpiTile label="Cash Balance" value={fmtMoney(cf.cashBalance)} sub={cf.hasCurrentBS ? 'as at period end' : 'no BS for this period'}
                     valueColor={cashColor(cf.cashBalance)} />
            <KpiTile label="Net Income (EBITDA)" value={fmtMoney(op.netIncome)} sub="proxy — pre-tax/interest"
                     valueColor={cashColor(op.netIncome)} />
            <KpiTile label="Depreciation + Amortis." value={fmtMoney(op.depreciationAmortisation)} sub="non-cash add-back" />
            <KpiTile label="Net Change in Cash"
                     value={cf.netChangeInCash == null ? '—' : fmtSigned(cf.netChangeInCash)}
                     sub={cf.hasPriorBS ? 'OCF + Inv + Fin' : 'needs prior BS'}
                     valueColor={cashColor(cf.netChangeInCash)} />
          </div>

          {/* Operating */}
          <div style={S.card}>
            <div style={S.cardTitle}>
              Operating Activities
              <span style={S.cardTotal}>{fmtMoney(op.operatingCashFlow)}</span>
            </div>
            <Row label="Net Income (EBITDA proxy)"   value={op.netIncome} />
            <Row label="+ Depreciation & Amortisation" value={op.depreciationAmortisation} sub="non-cash" />
            {op.workingCapitalChange != null ? (
              <>
                <Row label="± Δ Working Capital" value={op.workingCapitalChange} />
                {wcDetails && (
                  <>
                    <Row label="Δ Receivables" value={-wcDetails.dAR} indent
                         sub={wcDetails.dAR > 0 ? 'AR ↑ uses cash' : 'AR ↓ frees cash'} />
                    <Row label="Δ Inventory"   value={-wcDetails.dInv} indent
                         sub={wcDetails.dInv > 0 ? 'Inv ↑ uses cash' : 'Inv ↓ frees cash'} />
                    <Row label="Δ Payables"    value={wcDetails.dAP} indent
                         sub={wcDetails.dAP > 0 ? 'AP ↑ source' : 'AP ↓ uses cash'} />
                  </>
                )}
              </>
            ) : (
              <div style={S.placeholder}>± Δ Working Capital — pending multi-period BS</div>
            )}
            <Row label="Operating Cash Flow" value={op.operatingCashFlow} total />
          </div>

          {/* Investing */}
          <div style={S.card}>
            <div style={S.cardTitle}>
              Investing Activities
              <span style={S.cardTotal}>{inv.netInvestingCashFlow == null ? '—' : fmtMoney(inv.netInvestingCashFlow)}</span>
            </div>
            {inv.netInvestingCashFlow != null ? (
              <Row label="Δ Non-current Assets (capex proxy)" value={inv.netInvestingCashFlow} />
            ) : (
              <div style={S.placeholder}>Needs ≥ 2 BS periods. Once the multi-period BS pipeline ships, capex (Δ non-current assets) populates here.</div>
            )}
          </div>

          {/* Financing */}
          <div style={S.card}>
            <div style={S.cardTitle}>
              Financing Activities
              <span style={S.cardTotal}>{fin.netFinancingCashFlow == null ? '—' : fmtMoney(fin.netFinancingCashFlow)}</span>
            </div>
            {fin.netFinancingCashFlow != null ? (
              <Row label="Δ Long-term Debt + Δ Contributed Equity" value={fin.netFinancingCashFlow} />
            ) : (
              <div style={S.placeholder}>Needs ≥ 2 BS periods. Long-term debt movement + contributed equity populate here once available.</div>
            )}
          </div>

          {/* Net Change reconciliation */}
          <div style={S.card}>
            <div style={S.cardTitle}>Cash Reconciliation</div>
            <Row label="Operating Cash Flow"      value={op.operatingCashFlow} />
            <Row label="+ Investing Cash Flow"    value={inv.netInvestingCashFlow} />
            <Row label="+ Financing Cash Flow"    value={fin.netFinancingCashFlow} />
            <Row label="= Net Change in Cash"     value={cf.netChangeInCash} total />
            <Row label="Ending Cash Balance (BS)" value={cf.cashBalance} />
          </div>

          {/* Trend */}
          <div style={S.card}>
            <div style={S.cardTitle}>
              12-Month Operating Cash Flow (EBITDA proxy)
              <span style={S.cardTotal}>{trend?.trend?.length || 0} months</span>
            </div>
            <TrendChart trend={trend?.trend || []} />
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', marginTop: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Until multi-period BS lands, trend uses EBITDA per period — a close proxy for OCF for asset-light retail.
            </div>
          </div>

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', padding: '14px 4px 0', borderTop: '1px solid var(--card-border)', marginTop: 16, lineHeight: 1.55 }}>
            Cash flow uses the indirect method: net income adjusted for non-cash items + working-capital movement.
            Net Income is approximated by EBITDA (no tax/interest split in chart-of-accounts).
            D&A identified by GL description matching <code>depreciation</code> or <code>amortis*</code>.
          </div>
        </>
      )}
    </div>
  );
}

function KpiTile({ label, value, sub, valueColor }) {
  return (
    <div style={S.kpiCard}>
      <div style={S.kpiLabel}>{label}</div>
      <div style={{ ...S.kpiVal, ...(valueColor ? { color: valueColor } : {}) }}>{value}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}
